import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { useMinimumDelay } from '@hooks/use-minimum-delay';

import { RotatingLoadingMessages } from './rotating-loading-messages';

type RotatingLoadingMessagesContextValue = {
  show: (props: RotatingLoadingMessagesState) => void;
  hide: () => void;
  delayedState: RotatingLoadingMessagesState | null;
};

const RotatingLoadingMessagesContext = createContext<RotatingLoadingMessagesContextValue | null>(
  null
);

type RotatingLoadingMessagesProviderProps = {
  children: ReactNode;
};

type RotatingLoadingMessagesState = {
  title: string;
  messages: string[];
  minimumDelay?: number;
};

export const RotatingLoadingMessagesProvider = ({
  children,
}: RotatingLoadingMessagesProviderProps) => {
  const [state, setState] = useState<RotatingLoadingMessagesState | null>(null);
  const delayedState = useMinimumDelay(state);

  const show = useCallback(({ title, messages, minimumDelay }: RotatingLoadingMessagesState) => {
    setState({ title, messages, minimumDelay });
  }, []);

  const hide = useCallback(() => {
    setState(null);
  }, []);

  return (
    <RotatingLoadingMessagesContext.Provider value={{ show, hide, delayedState }}>
      {delayedState && (
        <RotatingLoadingMessages title={delayedState.title} messages={delayedState.messages} />
      )}
      {children}
    </RotatingLoadingMessagesContext.Provider>
  );
};

export const useRotatingLoadingMessagesContext = (): RotatingLoadingMessagesContextValue => {
  const context = useContext(RotatingLoadingMessagesContext);
  if (!context) {
    throw new Error(
      'useRotatingLoadingMessages must be used within RotatingLoadingMessagesProvider'
    );
  }
  return context;
};
